require('./stocksMeter2.scss');

const PropTypes = require('prop-types');

const React = require('react');
const d3 = require('d3');
const isEqual = require('lodash/lang/isEqual');
const reduce = require('lodash/collection/reduce');
const { formatText } = require('core/i18n').i18n;

// @helpers
const ViewportHelper = require('utilities/viewportHelper');

const radius = 122;
const barWidth = 10;

const startingAngle = 270;
const sinInOut = t => (1 - Math.cos(Math.PI * t)) / 2;

const degToRad = deg => deg / 180 * Math.PI;
// This function contemplates that we are working with half of a circle
const percToDeg = perc => perc * 1.8;

const percToRad = perc => degToRad(percToDeg(perc));
const needlePivotBorderColor = '#587580';
const needlePivotCenterColor = '#EEEEEE'; // $gallery-color sass variable
const needleColor = '#002744'; // $ok-color-multu-primary-second sass variable
const needlePivotRadius = 15;

const mkCmd = perc => {
    const thetaRad = percToRad(perc);
    const centerX = 0;
    const centerY = 0;
    const topX = centerX - radius * Math.cos(thetaRad);
    const topY = centerY - radius * Math.sin(thetaRad);
    const leftX = centerX - needlePivotRadius * Math.cos(thetaRad - Math.PI / 2);
    const leftY = centerY - needlePivotRadius * Math.sin(thetaRad - Math.PI / 2);
    const rightX = centerX - needlePivotRadius * Math.cos(thetaRad + Math.PI / 2);
    const rightY = centerY - needlePivotRadius * Math.sin(thetaRad + Math.PI / 2);

    return {topX, topY, leftX, leftY, rightX, rightY};
};

const needleCords = perc => {
    const {topX, topY, leftX, leftY, rightX, rightY} = mkCmd(perc);
    return `M ${leftX} ${leftY} L ${topX} ${topY} L ${rightX} ${rightY}`;
};

class StocksMeter2 extends React.Component {
    constructor(props) {
        super(props);

        this.state = { componentHasBeenViewed: false };

        this.createBar = this.createBar.bind(this);
        this.createNeedle = this.createNeedle.bind(this);
        this.createPercentage = this.createPercentage.bind(this);
        this.blackBox = this.blackBox.bind(this);
        this.setNode = this.setNode.bind(this);
        this.onComponentVisible = this.onComponentVisible.bind(this);

        this.componentInViewportHandler = null;
    }

    componentDidMount() {
        this.componentInViewportHandler = ViewportHelper.onElementInViewport(
            this.nodeReference,
            window,
            this.onComponentVisible
        );
    }

    componentDidUpdate(old) {
        const difference = reduce(this.props, function(result, value, key) {
            return isEqual(value, old[key]) ? result : result.concat(key);
        }, []);

        if (difference.length === 0) {
            return;
        }

        const props =
            this.state.componentHasBeenViewed ?
            this.props :
            this.getPropertiesToAvoidAnimation();

        d3.select(this.nodeReference).call(this.blackBox(props));
    }

    componentWillUnmount() {
        ViewportHelper.removeElementInViewportHandler(window, this.componentInViewportHandler);
    }

    onComponentVisible() {
        this.setState({ componentHasBeenViewed: true });

        ViewportHelper.removeElementInViewportHandler(window, this.componentInViewportHandler);
        d3.select(this.nodeReference).call(this.blackBox(this.props));
    }

    getPropertiesToAvoidAnimation() {
        return Object.assign({}, this.props, { percentage: 0, transitionDuration: 0 });
    }

    setNode(ref) {
        this.nodeReference = ref;
    }

    blackBox(props) {
        const {percentage, transitionDuration} = props;
        const container = this.nodeReference;

        d3.select(container).select('svg').remove();
        return me => {
            const paddingH = 40;
            const paddingTop = 30;
            const paddingBottom = 20;
            const svg = me
                .append('svg')
                .attr('width', radius * 2 + paddingH * 2)
                .attr('height', radius + paddingTop + paddingBottom)
                .style('overflow', 'visible')
                .style('padding', `${paddingTop}px ${paddingH}px ${paddingBottom}px`);

            const g = svg
                .append('g')
                .attr('transform', `translate(${radius} ${radius})`);

            this.createBar(g);
            const needle = this.createNeedle(g);
            const percentageNumber = this.createPercentage(g);

            // Percentage fadeIn effect
            percentageNumber.attr('fill-opacity', 0);
            // This prevents displaying a 0% before the component is shown in the viewport,
            // hence, before the animation starts.
            if (this.state.componentHasBeenViewed) {
                percentageNumber
                    .transition()
                    .delay(transitionDuration * 0.9)
                    .attr('fill-opacity', 1);
            }

            needle
                .transition()
                .duration(transitionDuration)
                .tween('progress', () => t =>
                    needle.attr('d', needleCords(percentage * sinInOut(t)))
                );
        };
    }

    createBar(g) {
        const colors = [
            '#99193f',
            '#fa0048',
            '#45c27d',
            '#3d7b4f',
        ];

        const barSeparationDegrees = 3;
        const barLengthDegrees = 180 / colors.length;
        colors.forEach((color, index) => {
            let start = barLengthDegrees * index + startingAngle;
            let end = start + barLengthDegrees;

            if (index !== 0) {
                start += barSeparationDegrees;
            }

            if (index !== colors.length - 1) {
                end -= barSeparationDegrees;
            }

            const arc = d3.svg
                .arc()
                .outerRadius(radius - barWidth)
                .innerRadius(radius)
                .startAngle(degToRad(start))
                .endAngle(degToRad(end));

            g.append('path').attr('d', arc).style('fill', color);
        });
    }

    createNeedle(g) {
        // The needle triangle is positioned and painted on the animation
        // logic, and makes uses of `needleCords` function to check its position.
        const needle = g.append('path').style('fill', needleColor);

        // outer circle
        g
            .append('circle')
            .attr('cx', 0)
            .attr('cy', 0)
            .attr('r', needlePivotRadius)
            .style('fill', needlePivotBorderColor);

        // inner circle
        g
            .append('circle')
            .attr('cx', 0)
            .attr('cy', 0)
            .attr('r', needlePivotRadius / 2)
            .style('fill', needlePivotCenterColor);

        return needle;
    }

    createPercentage(g) {
        const {percentage} = this.props;
        const {topX, topY} = mkCmd(percentage);

        const number = g
            .append('text')
            .attr('class', 'stocks-meter2__percentage')
            .attr('fill', needleColor)
            .attr('text-anchor', 'middle')
            .text(() => `${percentage}%`);

        const interpolateLeftX = d3.interpolate(-25, 0);
        const interpolateRightX = d3.interpolate(0, 30);

        const interpolateY = d3.interpolate(-3, -6);

        // These values indicates the margin between the percentage
        // number and the graph.
        let offsetX, offsetY;
        if (percentage < 10) { // section from 0 - 9 %
            offsetX = -20;
            offsetY = 5;
        } else if (percentage < 15) { // section from 10 - 14%
            const percToMiddle = (percentage - 10) / 40;
            offsetX = interpolateLeftX(percToMiddle);
            offsetY = interpolateY(percToMiddle);
        } else if (percentage < 40) { // section from 15 - 39%
            const percToMiddle = (percentage - 15) / 40;
            offsetX = interpolateLeftX(percToMiddle);
            offsetY = interpolateY(percToMiddle);
        } else if (percentage < 50) { // section from 40 - 49%
            const percToMiddle = (percentage - 10) / 40;
            offsetX = interpolateLeftX(percToMiddle);
            offsetY = interpolateY(percToMiddle);
        } else if (percentage < 60) { // section from 50 - 59%
            const percFromMiddle = percentage % 50 / 50;
            offsetX = interpolateRightX(percFromMiddle);
            offsetY = interpolateY(1 - percFromMiddle);
        } else if (percentage < 90) { // section from 60 - 89%
            const percFromMiddle = percentage % 50 / 50;
            offsetX = interpolateRightX(percFromMiddle);
            offsetY = interpolateY(1 - percFromMiddle);
        } else { // section from 90 - 100%
            if (percentage < 100) {
                offsetX = 25;
                offsetY = 5;
            } else {
                offsetX = 30;
                offsetY = 5;
            }
        }

        return number.attr('x', topX + offsetX).attr('y', topY + offsetY);
    }

    render() {
        const lowTrendMeterText = formatText('broker-marketTrendMeterLow');
        const highTrendMeterText = formatText('broker-marketTrendMeterHigh');

        return (
            <div className="stocks-meter2">
                <div className="stocks-meter2__graph" ref={this.setNode} />
                <div className="stocks-meter2__title-box">
                    <div className="stocks-meter2__title stocks-meter2__title--left">
                        {lowTrendMeterText}
                    </div>
                    <div className="stocks-meter2__title stocks-meter2__title--right">
                        {highTrendMeterText}
                    </div>
                </div>
            </div>
        );
    }
}

StocksMeter2.propTypes = {
    percentage: PropTypes.number.isRequired,
    transitionDuration: PropTypes.number,
};

StocksMeter2.defaultProps = {
    transitionDuration: 2000,
};

module.exports = StocksMeter2;
